import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import ReactTooltip from 'react-tooltip';
import styled from 'styled-components';
import { DamWeekDayKey } from '~/providers/types/api-private';
import { colors } from '~/styles/colors';
import {
  CheckBox, CheckRow, DayChip, DaysGrid, HiddenCheckbox, ToggleTrack,
} from '../ds';
import { DISPLAY_DAY_ORDER } from '../schedules';
import { CheckIcon } from './icons';

const DAY_LABEL_KEYS: Record<DamWeekDayKey, string> = {
  sun: 'diasDaSemana.dom',
  mon: 'diasDaSemana.seg',
  tue: 'diasDaSemana.ter',
  wed: 'diasDaSemana.qua',
  thu: 'diasDaSemana.qui',
  fri: 'diasDaSemana.sex',
  sat: 'diasDaSemana.sab',
};

export function getDayLabelKey(day: DamWeekDayKey): string {
  return DAY_LABEL_KEYS[day];
}

interface ToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  testId?: string;
}

export function Toggle({
  checked, onChange, label, testId,
}: Readonly<ToggleProps>): ReactElement {
  return (
    <ToggleTrack
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      $on={checked}
      onClick={() => onChange(!checked)}
      data-testid={testId}
    />
  );
}

const CheckTexts = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CheckTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.text['main-900']};
`;

const CheckHint = styled.span`
  font-size: 12px;
  color: ${colors.text['sub-350']};
`;

interface CheckboxFieldProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  hint?: string;
  disabled?: boolean;
}

export function CheckboxField({
  checked, onChange, label, hint, disabled,
}: Readonly<CheckboxFieldProps>): ReactElement {
  return (
    <CheckRow $disabled={disabled}>
      <HiddenCheckbox checked={checked} disabled={disabled} onChange={(event) => onChange(event.target.checked)} />
      <CheckBox $checked={checked}><CheckIcon size={14} strokeWidth={3} color={colors.text['white-0']} /></CheckBox>
      <CheckTexts>
        <CheckTitle>{label}</CheckTitle>
        {hint ? <CheckHint>{hint}</CheckHint> : null}
      </CheckTexts>
    </CheckRow>
  );
}

interface DayChipsProps {
  selected: DamWeekDayKey[];
  onToggle?: (day: DamWeekDayKey) => void;
}

/** Dias de domingo a sábado; com `onToggle` os chips são selecionáveis, sem ele são só exibição. */
export function DayChips({ selected, onToggle }: Readonly<DayChipsProps>): ReactElement {
  const { t } = useTranslation();
  return (
    <DaysGrid>
      {DISPLAY_DAY_ORDER.map((day) => (
        <DayChip
          key={day}
          type="button"
          $on={selected.includes(day)}
          aria-pressed={selected.includes(day)}
          disabled={!onToggle}
          onClick={() => onToggle?.(day)}
        >
          {t(DAY_LABEL_KEYS[day]).toUpperCase()}
        </DayChip>
      ))}
    </DaysGrid>
  );
}

const InfoCircle = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border: 1.4px solid ${colors.text['sub-350']};
  border-radius: 50%;
  font-size: 10px;
  font-weight: 700;
  font-style: italic;
  text-transform: none;
  color: ${colors.text['sub-350']};
  cursor: help;
`;

/** O tooltip fica dentro do rótulo do campo, que é em caixa alta: o texto da dica volta à caixa normal. */
const HintTooltip = styled(ReactTooltip)`
  text-transform: none;
  letter-spacing: normal;
  font-weight: 400;
`;

export function InfoHint({ id, text }: Readonly<{ id: string; text: string }>): ReactElement {
  return (
    <>
      <InfoCircle data-tip data-for={id} aria-label={text}>i</InfoCircle>
      <HintTooltip id={id} place="top" effect="solid">{text}</HintTooltip>
    </>
  );
}
